/**
 * VoicemailOverlay
 *
 * Shown when status === 'voicemail' — the caller reached a user who has DND on.
 * Sequence:
 *   1. Play a TTS greeting: "Hi, [Name] is unavailable…"
 *   2. Play a short beep
 *   3. Start recording from the caller's mic (MediaRecorder)
 *   4. Caller can Send (uploads + messages the callee) or Cancel
 *   5. Auto-send after 90 seconds
 *
 * Desktop — compact card, top-right below header.
 * Mobile  — full-screen overlay.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { Send, X, Mic, MicOff } from '@/shared/ui/icons';
import { UserAvatar } from '../../shared/ui/user-avatar';
import { useCall } from './CallContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const MAX_RECORD_SECONDS = 90;

// ─── Dark-mode detector ───────────────────────────────────────────────────────

function useIsDark() {
  const [dark, setDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setDark(document.documentElement.classList.contains('dark')),
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

function formatTime(s: number) {
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

// ── Beep via Web Audio API ────────────────────────────────────────────────────

function playBeep(): Promise<void> {
  return new Promise((resolve) => {
    try {
      const ctx = new AudioContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 1000;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.6);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.6);
      osc.onended = () => { ctx.close(); resolve(); };
    } catch {
      resolve();
    }
  });
}

// ── TTS greeting ─────────────────────────────────────────────────────────────

function speakGreeting(name: string): Promise<void> {
  return new Promise((resolve) => {
    if (!('speechSynthesis' in window)) { resolve(); return; }

    window.speechSynthesis.cancel();
    const text = `Hi, ${name} is unavailable right now. Please leave your message after the beep.`;
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.volume = 0.9;
    utterance.rate = 0.92;
    utterance.pitch = 1.0;

    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(
      (v) =>
        v.lang.startsWith('en') &&
        (v.name.includes('Samantha') ||
          v.name.includes('Karen') ||
          v.name.includes('Daniel') ||
          v.name.includes('Google UK') ||
          v.name.includes('Google US')),
    );
    if (preferred) utterance.voice = preferred;

    utterance.onend = () => resolve();
    utterance.onerror = () => resolve();
    window.speechSynthesis.speak(utterance);

    // Safety timeout — if TTS hangs, still proceed
    setTimeout(resolve, 8000);
  });
}

// ── Core hook — manages the recording lifecycle ───────────────────────────────

type Phase = 'greeting' | 'recording' | 'sending';

function useVoicemailRecorder() {
  const { remoteUser, sendVoicemail, cancelVoicemail } = useCall();
  const [phase, setPhase] = useState<Phase>('greeting');
  const [seconds, setSeconds] = useState(0);
  const [micOk, setMicOk] = useState(true);

  const recorderRef  = useRef<MediaRecorder | null>(null);
  const chunksRef    = useRef<Blob[]>([]);
  const timerRef     = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef    = useRef<MediaStream | null>(null);
  const secondsRef   = useRef(0);

  const stopRecorder = useCallback((): Blob | null => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (chunksRef.current.length === 0) return null;
    const mimeType = recorderRef.current?.mimeType || 'audio/webm';
    return new Blob(chunksRef.current, { type: mimeType });
  }, []);

  const handleSend = useCallback(() => {
    const blob = stopRecorder();
    if (!blob) { cancelVoicemail(); return; }
    setPhase('sending');
    sendVoicemail(blob, secondsRef.current);
  }, [stopRecorder, sendVoicemail, cancelVoicemail]);

  const handleCancel = useCallback(() => {
    stopRecorder();
    cancelVoicemail();
  }, [stopRecorder, cancelVoicemail]);

  // Start greeting → beep → record
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const name = remoteUser?.displayName || remoteUser?.name || 'this person';

      // 1. Greeting TTS
      await speakGreeting(name);
      if (cancelled) return;

      // 2. Beep
      await playBeep();
      if (cancelled) return;

      // 3. Get mic
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      } catch {
        setMicOk(false);
        return;
      }
      if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }

      streamRef.current = stream;
      chunksRef.current = [];

      // 4. Start MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : '';

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.start(250);
      setPhase('recording');

      // 5. Timer + auto-send
      timerRef.current = setInterval(() => {
        secondsRef.current += 1;
        setSeconds((s) => {
          const next = s + 1;
          if (next >= MAX_RECORD_SECONDS) {
            // Auto-send
            clearInterval(timerRef.current!);
            timerRef.current = null;
            recorder.stop();
            recorder.onstop = () => {
              if (cancelled) return;
              const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
              setPhase('sending');
              sendVoicemail(blob, secondsRef.current);
            };
          }
          return next;
        });
      }, 1000);
    })();

    return () => {
      cancelled = true;
      window.speechSynthesis?.cancel();
      if (timerRef.current) clearInterval(timerRef.current);
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { phase, seconds, micOk, handleSend, handleCancel };
}

// ── Shared body ───────────────────────────────────────────────────────────────

function VoicemailBody({ compact = false, isDark = true }: { compact?: boolean; isDark?: boolean }) {
  const { remoteUser } = useCall();
  const { phase, seconds, micOk, handleSend, handleCancel } = useVoicemailRecorder();
  const displayName = remoteUser?.displayName || remoteUser?.name || 'Unknown';

  const btnSize   = compact ? 44 : 64;
  const iconSize  = compact ? 16 : 24;
  const fontSize  = compact ? '1rem' : '1.3rem';

  const textPrimary   = isDark ? '#ffffff' : '#000000';
  const textSecondary = isDark ? 'rgba(255,255,255,0.55)' : '#8e8e93';
  const btnBg         = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';
  const btnColor      = isDark ? 'white' : '#1c1c1e';
  const labelColor    = isDark ? 'rgba(255,255,255,0.55)' : '#8e8e93';
  const waveColor     = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.35)';

  const statusLine =
    phase === 'greeting' ? 'Playing greeting…'
    : phase === 'sending' ? 'Sending voicemail…'
    : micOk
      ? `Recording ${formatTime(seconds)} / ${formatTime(MAX_RECORD_SECONDS)}`
      : 'Microphone unavailable';

  return (
    <div className={`flex flex-col items-center ${compact ? 'gap-4 px-5 py-5' : 'gap-6 px-6 py-8'}`}>
      {/* Avatar */}
      <div className="relative flex items-center justify-center">
        <span
          className="absolute rounded-full animate-ping opacity-20"
          style={{ width: compact ? 64 : 88, height: compact ? 64 : 88, background: 'var(--brand)' }}
        />
        <UserAvatar
          src={remoteUser?.avatar}
          name={displayName}
          size={compact ? 56 : 80}
          border={`3px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)'}`}
          fallbackBg={isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)'}
          fallbackColor={isDark ? 'white' : '#1c1c1e'}
          className="relative"
        />
      </div>

      {/* Name + label */}
      <div className="text-center">
        <p className="font-apple font-semibold" style={{ fontSize, color: textPrimary }}>
          {displayName}
        </p>
        <p className="font-apple mt-1" style={{ fontSize: 'var(--text-label)', color: textSecondary }}>
          Leave a voicemail
        </p>
      </div>

      {/* Recording indicator */}
      <div className="flex items-center gap-2 h-6">
        {phase === 'recording' && micOk ? (
          <>
            <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse flex-shrink-0" />
            <div className="flex items-center gap-0.5 h-5">
              {Array.from({ length: 18 }).map((_, i) => (
                <div
                  key={i}
                  className="w-0.5 rounded-full animate-pulse"
                  style={{
                    height: `${28 + Math.random() * 60}%`,
                    background: waveColor,
                    animationDuration: `${0.35 + Math.random() * 0.5}s`,
                    animationDelay: `${Math.random() * 0.4}s`,
                  }}
                />
              ))}
            </div>
            <span className="font-apple tabular-nums" style={{ fontSize: 'var(--text-label)', color: textSecondary }}>
              {statusLine}
            </span>
          </>
        ) : (
          <span className="font-apple" style={{ fontSize: 'var(--text-label)', color: textSecondary }}>
            {statusLine}
          </span>
        )}
      </div>

      {/* Actions — CSS grid, 3 equal columns */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: compact ? '0 20px' : '0 32px',
        width: '100%',
        maxWidth: compact ? 200 : 260,
      }}>
        {/* Cancel */}
        <div className="flex flex-col items-center gap-1.5">
          <button
            type="button"
            onClick={handleCancel}
            disabled={phase === 'sending'}
            className="active:opacity-70 transition-opacity cursor-pointer disabled:opacity-40"
            style={{ width: btnSize, height: btnSize, borderRadius: 999, background: btnBg, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' }}
          >
            <X style={{ width: iconSize, height: iconSize, color: btnColor }} />
          </button>
          <span className="font-apple" style={{ fontSize: 'var(--text-search)', color: labelColor }}>Cancel</span>
        </div>

        {/* Mic (decorative status) */}
        <div className="flex flex-col items-center gap-1.5">
          <div style={{ width: btnSize, height: btnSize, borderRadius: 999, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {micOk
              ? <Mic    style={{ width: iconSize, height: iconSize, color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)' }} />
              : <MicOff style={{ width: iconSize, height: iconSize, color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.25)' }} />
            }
          </div>
          <span className="font-apple" style={{ fontSize: 'var(--text-search)', color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)' }}>Mic</span>
        </div>

        {/* Send */}
        <div className="flex flex-col items-center gap-1.5">
          <button
            type="button"
            onClick={handleSend}
            disabled={phase !== 'recording' || !micOk}
            className="active:opacity-70 transition-opacity cursor-pointer disabled:opacity-40"
            style={{
              width: btnSize, height: btnSize, borderRadius: 999,
              background: phase === 'recording' && micOk ? 'var(--brand)' : btnBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: 'none',
              boxShadow: phase === 'recording' && micOk ? '0 4px 16px color-mix(in srgb, var(--brand) 45%, transparent)' : 'none',
            }}
          >
            <Send style={{ width: iconSize, height: iconSize, color: phase === 'recording' && micOk ? 'white' : btnColor }} />
          </button>
          <span className="font-apple" style={{ fontSize: 'var(--text-search)', color: labelColor }}>Send</span>
        </div>
      </div>
    </div>
  );
}

// ── Mobile full-screen ────────────────────────────────────────────────────────

function MobileVoicemailScreen() {
  const isDark = useIsDark();

  const bg = isDark
    ? 'linear-gradient(160deg, #2d1246 0%, #1a0a2e 60%, #0f0620 100%)'
    : 'linear-gradient(180deg, #f2f2f7 0%, #ffffff 60%)';
  const labelColor = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)';

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center"
      style={{
        background: bg,
        paddingTop:    'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <p className="font-apple uppercase mb-6" style={{ fontSize: '0.65rem', letterSpacing: '0.14em', color: labelColor }}>
        Voicemail
      </p>
      <VoicemailBody isDark={isDark} />
    </div>
  );
}

// ── Desktop card top-right ────────────────────────────────────────────────────

function DesktopVoicemailCard() {
  return (
    <div
      className="fixed z-[200] pointer-events-auto"
      style={{ top: 'calc(var(--qs-web-header-height, 64px) + 8px)', right: '16px' }}
    >
      <div
        className="w-72 rounded-2xl overflow-hidden border border-white/10"
        style={{
          background: 'linear-gradient(145deg, #3d1860 0%, #1e0d36 100%)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.35)',
        }}
      >
        <div className="h-0.5 w-full" style={{ background: 'var(--brand)' }} />
        <div className="flex items-center gap-2 px-4 pt-3 pb-1">
          <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
          <span className="font-apple text-white/50 uppercase tracking-widest" style={{ fontSize: '0.6rem', letterSpacing: '0.12em' }}>
            Voicemail
          </span>
        </div>
        <VoicemailBody compact />
      </div>
    </div>
  );
}

// ── Export ────────────────────────────────────────────────────────────────────

export function VoicemailOverlay() {
  const { status } = useCall();
  // 820px = iPad Air portrait. Intentional — see ActiveCallBar for
  // rationale (portrait tablets share the phone full-screen call UI).
  const isMobile   = useMediaQuery('(max-width: 820px)');

  if (status !== 'voicemail') return null;

  return isMobile ? <MobileVoicemailScreen /> : <DesktopVoicemailCard />;
}
